import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  BadgeColors,
  Button,
  ButtonVariants,
  Icon,
  IconNames,
} from '@triumph/design-system';
import { useAppContext } from '@/app/components/providers/AppContextProvider';
import { getNoaDocumentDownloadUrl } from '@/app/shared/api/endpoints/noaCenter';
import { formatShortDate } from '@/app/shared/utils/tableUtils';
import useExportToFile from '@/app/shared/utils/useExportToFile';
import { FileType } from '@/app/shared/constants/misc/FileType';
import { badgeColor } from '@/app/shared/utils/noaUtils';
import { CarrierBadge, NoaDocument, NoaDocumentType } from '../noaCenter.types';
import {
  DocumentActions,
  DocumentBadgeRow,
  DocumentCard,
  DocumentCardHeader,
  DocumentMetaLabel,
  DocumentMetaRow,
  DocumentMetaValue,
  DocumentName,
  DocumentTitleBlock,
  DocumentUnavailableNote,
  DocumentVersion,
} from '../NOACenter.StyledComponents';

interface NoaDocumentCardProps {
  document: NoaDocument;
  carrierName: string;
  statusBadge: CarrierBadge;
  isReleased?: boolean;
}

export const NoaDocumentCard: React.FC<NoaDocumentCardProps> = ({
  document: doc,
  carrierName,
  statusBadge,
  isReleased = false,
}) => {
  const { t } = useTranslation('noaCenter');
  const { theme } = useAppContext();
  const isDownloadable =
    (doc.isDownloadable ?? true) && statusBadge !== CarrierBadge.Pending;
  const { exportFile } = useExportToFile();
  const [isViewing, setIsViewing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const isLorDocument = doc.documentType
    ? doc.documentType === NoaDocumentType.Lor ||
      doc.documentType === NoaDocumentType.Slor
    : isReleased;

  const documentName =
    doc.documentName ??
    `${isLorDocument ? t('document.lorTitle') : t('document.noaTitle')} — ${carrierName}`;

  const openUrl = (url: string) => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.click();
  };

  const handleView = async () => {
    if (isViewing) return;
    setIsViewing(true);
    try {
      const url = await getNoaDocumentDownloadUrl(doc.documentId);
      openUrl(url);
    } catch (error) {
      console.error('Failed to open NOA document:', error);
    } finally {
      setIsViewing(false);
    }
  };

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const url = await getNoaDocumentDownloadUrl(doc.documentId);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Document fetch failed: ${response.status}`);
      }
      const blob = await response.blob();
      await exportFile(documentName, blob, FileType.Pdf);
    } catch (error) {
      console.error('Failed to download NOA document:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <DocumentCard highlight={doc.isLatest} data-testid={`noa-document-${doc.documentId}`}>
      <DocumentCardHeader>
        <Icon
          name={IconNames.Filetype_Document}
          id={`noa-document-icon-${doc.documentId}`}
          size="md"
          color={theme.colors.greyscale.gray40}
        />
        <DocumentTitleBlock>
          <DocumentName>{documentName}</DocumentName>
          {doc.version && <DocumentVersion>{doc.version}</DocumentVersion>}
        </DocumentTitleBlock>
        <DocumentBadgeRow>
          {doc.isLatest && (
            <Badge label={t('document.latest')} color={BadgeColors.PRIMARY} />
          )}
          <Badge label={statusBadge} color={badgeColor(statusBadge)} />
        </DocumentBadgeRow>
      </DocumentCardHeader>

      {doc.issuedDate && (
        <DocumentMetaRow>
          <DocumentMetaLabel>{t('document.issued')}</DocumentMetaLabel>
          <DocumentMetaValue>{formatShortDate(doc.issuedDate)}</DocumentMetaValue>
        </DocumentMetaRow>
      )}
      {doc.effectiveDate && (
        <DocumentMetaRow>
          <DocumentMetaLabel>{t('document.effective')}</DocumentMetaLabel>
          <DocumentMetaValue>{formatShortDate(doc.effectiveDate)}</DocumentMetaValue>
        </DocumentMetaRow>
      )}

      <DocumentActions>
        <Button
          variant={ButtonVariants.secondary}
          icon={IconNames.Interface_LinkFile}
          iconLeft
          onClick={handleView}
          disabled={!isDownloadable || isViewing}
          data-testid={`noa-document-view-${doc.documentId}`}
        >
          {t('document.view')}
        </Button>
        <Button
          variant={ButtonVariants.primary}
          icon={IconNames.Interface_Download}
          iconLeft
          onClick={handleDownload}
          disabled={!isDownloadable || isDownloading}
          data-testid={`noa-document-download-${doc.documentId}`}
        >
          {isDownloading ? t('document.loading') : t('document.download')}
        </Button>
      </DocumentActions>
      {!isDownloadable && (
        <DocumentUnavailableNote data-testid={`noa-document-unavailable-${doc.documentId}`}>
          {t('document.notAvailable')}
        </DocumentUnavailableNote>
      )}
    </DocumentCard>
  );
};
